import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { onCallSignal, useSendCallSignal, type CallSignalEvent } from "@/lib/realtime";
import { toast } from "sonner";

export type CallStatus = "idle" | "outgoing-ringing" | "incoming-ringing" | "connecting" | "active";

export interface ActiveCall {
  conversationId: string;
  peerUserId: string;
  withVideo: boolean;
}

interface OfferPayload {
  sdp: RTCSessionDescriptionInit;
  withVideo: boolean;
  /** Set by useGroupCall when it sends an offer as part of group-call
   * signaling - this hook (1:1 only) must never treat that as an incoming
   * 1:1 call, since both hooks are mounted simultaneously and subscribe to
   * the same onCallSignal stream. */
  isGroupCall?: boolean;
}
interface AnswerPayload {
  sdp: RTCSessionDescriptionInit;
}
interface IceCandidatePayload {
  candidate: RTCIceCandidateInit;
}
interface EndPayload {
  reason?: "ended" | "unavailable" | "busy" | "failed";
}
interface RejectPayload {
  reason?: "declined" | "busy";
}

interface CallContextValue {
  status: CallStatus;
  call: ActiveCall | null;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  isMuted: boolean;
  isCameraOff: boolean;
  /** Set right when a call ends/fails/gets rejected, for the overlay to show a
   * one-line reason before it disappears. Cleared the next time a call starts. */
  lastEndReason: string | null;
  startCall: (conversationId: string, peerUserId: string, withVideo: boolean) => Promise<void>;
  acceptCall: () => Promise<void>;
  declineCall: () => void;
  hangUp: () => void;
  toggleMute: () => void;
  toggleCamera: () => void;
}

const CallContext = createContext<CallContextValue | null>(null);

// Fetched once per page load and cached - the list doesn't change while the app
// is open, and every call needs it, so there's no reason to re-fetch per call.
let stunServersPromise: Promise<string[]> | null = null;
function getStunServers(): Promise<string[]> {
  if (!stunServersPromise) {
    stunServersPromise = api
      .get<{ stunServers: string[] }>("/api/calls/config")
      .then((r) => r.stunServers)
      .catch(() => ["stun:stun.l.google.com:19302"]);
  }
  return stunServersPromise;
}

function userFacingEndReason(reason: string | undefined): string | null {
  switch (reason) {
    case "declined":
      return "Call declined";
    case "busy":
      return "User is on another call";
    case "unavailable":
      return "User is unavailable";
    case "failed":
      return "Call failed to connect";
    default:
      return null;
  }
}

export function CallProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const sendCallSignal = useSendCallSignal();

  const [status, setStatus] = useState<CallStatus>("idle");
  const [call, setCall] = useState<ActiveCall | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [lastEndReason, setLastEndReason] = useState<string | null>(null);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const pendingCandidatesRef = useRef<RTCIceCandidateInit[]>([]);
  const remoteDescriptionSetRef = useRef(false);
  // The pending incoming offer while status === "incoming-ringing" - only
  // materialized into a real RTCPeerConnection once the person actually accepts,
  // so declining (or ignoring) an unwanted call never touches the camera/mic.
  const pendingOfferRef = useRef<{ conversationId: string; fromUserId: string; sdp: RTCSessionDescriptionInit; withVideo: boolean } | null>(null);

  // Always current inside the onCallSignal listener below, which is registered
  // once and would otherwise close over stale state from its first render.
  const stateRef = useRef({ status, call });
  stateRef.current = { status, call };

  const cleanup = useCallback((reason: string | null) => {
    pcRef.current?.close();
    pcRef.current = null;
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    pendingCandidatesRef.current = [];
    remoteDescriptionSetRef.current = false;
    pendingOfferRef.current = null;
    setLocalStream(null);
    setRemoteStream(null);
    setIsMuted(false);
    setIsCameraOff(false);
    setCall(null);
    setStatus("idle");
    setLastEndReason(reason);
  }, []);

  const createPeerConnection = useCallback(
    async (toUserId: string, conversationId: string) => {
      const stunServers = await getStunServers();
      const pc = new RTCPeerConnection({ iceServers: [{ urls: stunServers }] });

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          sendCallSignal({
            type: "call:ice-candidate",
            toUserId,
            conversationId,
            payload: { candidate: event.candidate.toJSON() } satisfies IceCandidatePayload
          });
        }
      };

      pc.ontrack = (event) => {
        setRemoteStream(event.streams[0] ?? null);
      };

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === "connected") {
          setStatus("active");
        } else if (pc.connectionState === "failed") {
          sendCallSignal({ type: "call:end", toUserId, conversationId, payload: { reason: "failed" } satisfies EndPayload });
          cleanup("Call failed to connect");
        }
      };

      pcRef.current = pc;
      return pc;
    },
    [sendCallSignal, cleanup]
  );

  const flushPendingCandidates = useCallback(async () => {
    const pc = pcRef.current;
    if (!pc) return;
    for (const candidate of pendingCandidatesRef.current) {
      try {
        await pc.addIceCandidate(candidate);
      } catch {
        // A candidate that no longer applies (e.g. connection already moved on)
        // is not worth surfacing - WebRTC negotiation is expected to shed a few.
      }
    }
    pendingCandidatesRef.current = [];
  }, []);

  const startCall = useCallback(
    async (conversationId: string, peerUserId: string, withVideo: boolean) => {
      if (stateRef.current.status !== "idle") return;
      setLastEndReason(null);

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: withVideo });
      } catch {
        toast(withVideo ? "Couldn't access camera/microphone" : "Couldn't access microphone");
        return;
      }
      localStreamRef.current = stream;
      setLocalStream(stream);
      setCall({ conversationId, peerUserId, withVideo });
      setStatus("outgoing-ringing");

      const pc = await createPeerConnection(peerUserId, conversationId);
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      sendCallSignal({
        type: "call:offer",
        toUserId: peerUserId,
        conversationId,
        payload: { sdp: offer, withVideo } satisfies OfferPayload
      });
    },
    [createPeerConnection, sendCallSignal]
  );

  const acceptCall = useCallback(async () => {
    const pending = pendingOfferRef.current;
    if (!pending || stateRef.current.status !== "incoming-ringing") return;

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: pending.withVideo });
    } catch {
      toast(pending.withVideo ? "Couldn't access camera/microphone" : "Couldn't access microphone");
      sendCallSignal({
        type: "call:reject",
        toUserId: pending.fromUserId,
        conversationId: pending.conversationId,
        payload: { reason: "declined" } satisfies RejectPayload
      });
      cleanup(null);
      return;
    }
    localStreamRef.current = stream;
    setLocalStream(stream);
    setStatus("connecting");

    const pc = await createPeerConnection(pending.fromUserId, pending.conversationId);
    stream.getTracks().forEach((track) => pc.addTrack(track, stream));

    await pc.setRemoteDescription(pending.sdp);
    remoteDescriptionSetRef.current = true;
    await flushPendingCandidates();

    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);

    sendCallSignal({
      type: "call:answer",
      toUserId: pending.fromUserId,
      conversationId: pending.conversationId,
      payload: { sdp: answer } satisfies AnswerPayload
    });

    pendingOfferRef.current = null;
  }, [createPeerConnection, flushPendingCandidates, sendCallSignal, cleanup]);

  const declineCall = useCallback(() => {
    const pending = pendingOfferRef.current;
    if (pending) {
      sendCallSignal({
        type: "call:reject",
        toUserId: pending.fromUserId,
        conversationId: pending.conversationId,
        payload: { reason: "declined" } satisfies RejectPayload
      });
    }
    cleanup(null);
  }, [sendCallSignal, cleanup]);

  const hangUp = useCallback(() => {
    const current = stateRef.current.call;
    if (current) {
      sendCallSignal({
        type: "call:end",
        toUserId: current.peerUserId,
        conversationId: current.conversationId,
        payload: { reason: "ended" } satisfies EndPayload
      });
    }
    cleanup(null);
  }, [sendCallSignal, cleanup]);

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const nextMuted = !isMuted;
    stream.getAudioTracks().forEach((t) => (t.enabled = !nextMuted));
    setIsMuted(nextMuted);
  }, [isMuted]);

  const toggleCamera = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const nextOff = !isCameraOff;
    stream.getVideoTracks().forEach((t) => (t.enabled = !nextOff));
    setIsCameraOff(nextOff);
  }, [isCameraOff]);

  useEffect(() => {
    const unsubscribe = onCallSignal((event: CallSignalEvent) => {
      const { status: currentStatus, call: currentCall } = stateRef.current;

      // call:offer is the only signal type that can start a brand new call while
      // idle - everything else only makes sense as part of a call already in
      // progress, so it's ignored unless it matches the call currently underway.
      // This also naturally rejects a spoofed/stale message for a call that's
      // already over.
      if (event.type === "call:offer") {
        // Not for this hook - useGroupCall handles its own offers, tagged
        // this way specifically so this 1:1 system never mistakes one for an
        // incoming call (and, worse, never auto-declines it as "busy" on
        // behalf of a call this hook was never part of).
        if ((event.payload as OfferPayload | undefined)?.isGroupCall) return;

        if (currentStatus !== "idle") {
          // Already on a call (or already ringing) - decline automatically rather
          // than silently dropping it, so the other person's UI doesn't hang.
          sendCallSignal({
            type: "call:reject",
            toUserId: event.fromUserId,
            conversationId: event.conversationId,
            payload: { reason: "busy" } satisfies RejectPayload
          });
          return;
        }
        const payload = event.payload as OfferPayload;
        pendingOfferRef.current = {
          conversationId: event.conversationId,
          fromUserId: event.fromUserId,
          sdp: payload.sdp,
          withVideo: payload.withVideo
        };
        setLastEndReason(null);
        setCall({ conversationId: event.conversationId, peerUserId: event.fromUserId, withVideo: payload.withVideo });
        setStatus("incoming-ringing");
        sendCallSignal({ type: "call:ringing", toUserId: event.fromUserId, conversationId: event.conversationId, payload: {} });
        return;
      }

      if (!currentCall || currentCall.conversationId !== event.conversationId || currentCall.peerUserId !== event.fromUserId) {
        return;
      }

      if (event.type === "call:ringing") {
        // Purely informational - the caller's UI already shows "Calling..." from
        // outgoing-ringing; nothing to change here beyond what's already true.
        return;
      }

      if (event.type === "call:answer") {
        const pc = pcRef.current;
        if (!pc || currentStatus !== "outgoing-ringing") return;
        const payload = event.payload as AnswerPayload;
        setStatus("connecting");
        pc.setRemoteDescription(payload.sdp)
          .then(() => {
            remoteDescriptionSetRef.current = true;
            return flushPendingCandidates();
          })
          .catch(() => cleanup("Call failed to connect"));
        return;
      }

      if (event.type === "call:ice-candidate") {
        const payload = event.payload as IceCandidatePayload;
        if (remoteDescriptionSetRef.current && pcRef.current) {
          pcRef.current.addIceCandidate(payload.candidate).catch(() => {
            // Same reasoning as flushPendingCandidates - a stale candidate is fine to drop.
          });
        } else {
          pendingCandidatesRef.current.push(payload.candidate);
        }
        return;
      }

      if (event.type === "call:reject") {
        const payload = event.payload as RejectPayload;
        cleanup(userFacingEndReason(payload.reason) ?? "Call declined");
        return;
      }

      if (event.type === "call:end") {
        const payload = event.payload as EndPayload;
        cleanup(userFacingEndReason(payload.reason) ?? "Call ended");
        return;
      }
    });

    return unsubscribe;
    // sendCallSignal/cleanup/flushPendingCandidates are stable (useCallback with
    // stable deps), so this only needs to run once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Defensive: hang up cleanly if the provider itself ever unmounts mid-call
  // (e.g. logout) rather than leaving a dangling RTCPeerConnection and open
  // camera/mic.
  useEffect(() => {
    return () => {
      pcRef.current?.close();
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  // A logged-out user obviously can't be on a call - if auth is lost mid-call
  // (session expired, manual logout), tear it down rather than leaving media
  // devices open with no way to hang up from the UI.
  useEffect(() => {
    if (!user && stateRef.current.status !== "idle") cleanup(null);
  }, [user, cleanup]);

  const value: CallContextValue = {
    status,
    call,
    localStream,
    remoteStream,
    isMuted,
    isCameraOff,
    lastEndReason,
    startCall,
    acceptCall,
    declineCall,
    hangUp,
    toggleMute,
    toggleCamera
  };

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}

export function useCall() {
  const ctx = useContext(CallContext);
  if (!ctx) throw new Error("useCall must be used within CallProvider");
  return ctx;
}
